import { CandlestickData } from "lightweight-charts"
import { SpecialChart } from "./SpecialChart"

export class LineBreakChart extends SpecialChart {
    lineCount: number = 3

    constructor(options: { candles: CandlestickData[], lineCount?: number }) {
        super(options.candles)
        if (options.lineCount) this.lineCount = options.lineCount
    }

    getCandles(): CandlestickData[] {
        const candlesticks = this.candles
        if (candlesticks.length === 0) return [];
        const lineBreakCandles: CandlestickData[] = []

        // The first candle is always a line
        const firstCandle = candlesticks[0]

        lineBreakCandles.push({
            ...firstCandle,
            open: firstCandle.open,
            close: firstCandle.close,
            high: Math.max(firstCandle.open, firstCandle.close),
            low: Math.min(firstCandle.open, firstCandle.close)
        })

        for (let i = 1; i < candlesticks.length; i++) {
            const currentCandle = candlesticks[i];
            const lastLine = lineBreakCandles[lineBreakCandles.length - 1];
            const lastClose = lastLine.close;
            const currentClose = currentCandle.close;

            // Determine if valid line
            let isNewLine = false;
            let newLine: CandlestickData | null = null;

            const lastLineIsUp = lastLine.close >= lastLine.open;

            if (lastLineIsUp) {
                // Continuation: Close is higher than last Close
                if (currentClose > lastClose) {
                    isNewLine = true;
                    newLine = {
                        ...currentCandle,
                        open: lastClose,
                        close: currentClose,
                        high: currentClose,
                        low: lastClose
                    }
                }
                // Reversal: Close must be LOWER than the LOW of the last [lineCount] lines.
                else if (currentClose < lastClose) {
                    const lookback = Math.min(lineBreakCandles.length, this.lineCount);
                    let lowestLow = Infinity;
                    for (let j = 0; j < lookback; j++) {
                        const pastLine = lineBreakCandles[lineBreakCandles.length - 1 - j];
                        const low = Math.min(pastLine.open, pastLine.close);
                        if (low < lowestLow) lowestLow = low;
                    }

                    if (currentClose < lowestLow) {
                        isNewLine = true;
                        newLine = {
                            ...currentCandle,
                            // For reversal up, Start at last Open (Bottom of red candle here as last was UP/Green), End at Current Close
                            // User Request: "starts from the open of the red candle" (referring to a Green candle following a Red one).
                            open: lastLine.open,
                            close: currentClose,
                            high: lastLine.open,
                            low: currentClose
                        }
                    }
                }
            } else {
                // Last line was DOWN
                // Continuation: Close is lower than last Close
                if (currentClose < lastClose) {
                    isNewLine = true;
                    newLine = {
                        ...currentCandle,
                        open: lastClose,
                        close: currentClose,
                        high: lastClose,
                        low: currentClose
                    }
                }
                // Reversal: Close must be HIGHER than the HIGH of the last [lineCount] lines
                else if (currentClose > lastClose) {
                    // Check reversal condition
                    const lookback = Math.min(lineBreakCandles.length, this.lineCount);
                    let highestHigh = -Infinity;
                    for (let j = 0; j < lookback; j++) {
                        const pastLine = lineBreakCandles[lineBreakCandles.length - 1 - j];
                        const high = Math.max(pastLine.open, pastLine.close);
                        if (high > highestHigh) highestHigh = high;
                    }

                    if (currentClose > highestHigh) {
                        isNewLine = true;
                        newLine = {
                            ...currentCandle,
                            // For reversal up, Start at last Open (Top of red candle), End at Current Close
                            open: lastLine.open,
                            close: currentClose,
                            high: currentClose,
                            low: lastLine.open
                        }
                    }
                }
            }

            if (isNewLine && newLine) {
                newLine.high = Math.max(newLine.open, newLine.close);
                newLine.low = Math.min(newLine.open, newLine.close);
                lineBreakCandles.push(newLine);
            }
        }

        return lineBreakCandles
    }
}